import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Bell, Shield, Globe, Link2, Zap, AlertTriangle, CheckCircle2,
  XCircle, Eye, RefreshCw, ShieldOff, Clock, ExternalLink, FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import api from '@/lib/api';



const ALERT_ICONS = {
  domain_down: Globe, ssl_expiring: Shield, ssl_invalid: ShieldOff,
  redirect_broken: Link2, affiliate_unreachable: ExternalLink,
  ai_job_failed: Zap, thin_content: FileText, domain_slow: Clock,
};

const SEVERITY_COLORS = {
  critical: 'border-l-red-400 bg-red-400/5',
  warning: 'border-l-amber-400 bg-amber-400/5',
  info: 'border-l-blue-400 bg-blue-400/5',
};

export default function MonitoringPage() {
  const [alerts, setAlerts] = useState([]);
  const [sites, setSites] = useState([]);
  const [statusFilter, setStatusFilter] = useState('open');
  const [siteFilter, setSiteFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (siteFilter !== 'all') params.site_id = siteFilter;
      const [alertsRes, sitesRes] = await Promise.all([
        api.get('/monitoring/alerts', { params }),
        api.get('/sites'),
      ]);
      setAlerts(alertsRes.data);
      setSites(sitesRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [statusFilter, siteFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRunAll = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/monitoring/run-all');
      toast.success(data.message);
      setTimeout(loadData, 1000);
    } catch (err) {
      toast.error('Tarama basarisiz');
    } finally {
      setRunning(false);
    }
  };

  const handleAck = async (alertId) => {
    try {
      await api.post(`/monitoring/alerts/${alertId}/ack`);
      toast.success('Alert onaylandi');
      loadData();
    } catch (err) { toast.error('Islem basarisiz'); }
  };

  const handleResolve = async (alertId) => {
    try {
      await api.post(`/monitoring/alerts/${alertId}/resolve`);
      toast.success('Alert cozuldu');
      loadData();
    } catch (err) { toast.error('Islem basarisiz'); }
  };

  const getSiteName = (siteId) => sites.find(s => s.id === siteId)?.brand_name || siteId?.slice(0, 8);

  const openAlerts = alerts.filter(a => a.status === 'open');
  const criticalCount = openAlerts.filter(a => a.severity === 'critical').length;
  const warningCount = openAlerts.filter(a => a.severity === 'warning').length;

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight font-['Space_Grotesk']">Izleme & Alertler</h1>
          <p className="text-sm text-muted-foreground mt-1">{openAlerts.length} acik alert</p>
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px] bg-secondary/40 border-border/70"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tum Durumlar</SelectItem>
              <SelectItem value="open">Acik</SelectItem>
              <SelectItem value="acknowledged">Onaylandi</SelectItem>
              <SelectItem value="resolved">Cozuldu</SelectItem>
            </SelectContent>
          </Select>
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger className="w-[200px] bg-secondary/40 border-border/70"><SelectValue placeholder="Site" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tum Siteler</SelectItem>
              {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.brand_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={handleRunAll} disabled={running} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] gap-2 press-active">
            <RefreshCw className={`w-4 h-4 ${running ? 'animate-spin' : ''}`} /> Tum Siteleri Tara
          </Button>
        </div>
      </motion.div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-border/70">
          <CardContent className="pt-5"><p className="text-xs text-muted-foreground uppercase">Kritik</p><p className="text-2xl font-semibold mt-1 text-red-400">{criticalCount}</p></CardContent>
        </Card>
        <Card className="border-border/70">
          <CardContent className="pt-5"><p className="text-xs text-muted-foreground uppercase">Uyari</p><p className="text-2xl font-semibold mt-1 text-amber-400">{warningCount}</p></CardContent>
        </Card>
        <Card className="border-border/70">
          <CardContent className="pt-5"><p className="text-xs text-muted-foreground uppercase">Toplam Alert</p><p className="text-2xl font-semibold mt-1">{alerts.length}</p></CardContent>
        </Card>
      </div>

      {/* Alert List */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 rounded-xl shimmer" />)}</div>
      ) : alerts.length === 0 ? (
        <Card className="border-border/70">
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-400/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Hersey yolunda! Alert yok.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {alerts.map(alert => {
            const Icon = ALERT_ICONS[alert.type] || Bell;
            const colors = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.info;
            return (
              <Card key={alert.id} className={`border-border/70 border-l-2 ${colors}`}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <Icon className={`w-5 h-5 mt-0.5 ${alert.severity === 'critical' ? 'text-red-400' : alert.severity === 'warning' ? 'text-amber-400' : 'text-blue-400'}`} />
                      <div>
                        <p className="text-sm font-medium">{alert.message}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[10px] text-muted-foreground">{getSiteName(alert.site_id)}</span>
                          <span className="text-[10px] text-muted-foreground">{formatDate(alert.created_at)}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${alert.status === 'open' ? 'bg-red-400/10 text-red-400' : alert.status === 'acknowledged' ? 'bg-amber-400/10 text-amber-400' : 'bg-emerald-400/10 text-emerald-400'}`}>
                            {alert.status === 'open' ? 'Acik' : alert.status === 'acknowledged' ? 'Goruldu' : 'Cozuldu'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {alert.status === 'open' && (
                        <Button size="sm" variant="outline" className="text-xs border-border/70" onClick={() => handleAck(alert.id)}>Gordum</Button>
                      )}
                      {(alert.status === 'open' || alert.status === 'acknowledged') && (
                        <Button size="sm" className="text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => handleResolve(alert.id)}>Cozuldu</Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
